/**
 * 149-cw-r4 (PROMPT 16-PRESENCE-SESSION-R1): Global idle-timeout provider.
 *
 * Responsibilities:
 *   • Listen for REAL user activity (click, keydown, scroll, touchstart) — background
 *     polling/heartbeats do NOT count.
 *   • Throttle `POST /auth/extend-session` to at most 1/minute during active use.
 *   • Local countdown showing warning dialog `warning_minutes` before idle_timeout.
 *   • BroadcastChannel sync across tabs on same browser — activity on any tab
 *     extends the whole browser session; on logout, all tabs get kicked.
 *   • On timeout expiry: call auth.logout() (which hard-navigates to /login).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const BC_NAME = "ens_idle_channel_v1";
const THROTTLE_MS = 60_000;   // 1/min max server touches during active use

export default function IdleTimeoutProvider({ children }) {
  const { user, logout } = useAuth();
  const [cfg, setCfg] = useState({ idle_timeout_minutes: 30, warning_minutes: 2 });
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSec, setRemainingSec] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const lastServerTouchRef = useRef(0);
  const bcRef = useRef(null);
  const isLoggedIn = !!user;

  // Load config on mount + when user changes
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/security-session");
        if (!cancelled) setCfg({ idle_timeout_minutes: data.idle_timeout_minutes, warning_minutes: data.warning_minutes });
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn, user?.id]);

  // BroadcastChannel setup
  useEffect(() => {
    if (!isLoggedIn || typeof BroadcastChannel === "undefined") return;
    const ch = new BroadcastChannel(BC_NAME);
    bcRef.current = ch;
    ch.onmessage = (ev) => {
      const msg = ev.data || {};
      if (msg.type === "activity" && msg.ts) {
        lastActivityRef.current = Math.max(lastActivityRef.current, msg.ts);
        setShowWarning(false);
      } else if (msg.type === "logout") {
        // Peer tab logged out — do local cleanup + navigate.
        try { logout(); } catch { /* ignore */ }
      }
    };
    return () => { try { ch.close(); } catch { /* ignore */ } bcRef.current = null; };
  }, [isLoggedIn, logout]);

  // Register real activity events. Throttle server touches to 1/min. Broadcast to peers.
  useEffect(() => {
    if (!isLoggedIn) return;
    const record = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      if (showWarning) setShowWarning(false);
      // Broadcast to peers so multi-tab activity coalesces.
      try { bcRef.current?.postMessage({ type: "activity", ts: now }); } catch { /* ignore */ }
      // Throttle server touch to 1/min during active use.
      if (now - lastServerTouchRef.current > THROTTLE_MS) {
        lastServerTouchRef.current = now;
        api.post("/auth/extend-session").then(({ data }) => {
          if (data?.idle_timeout_minutes) {
            setCfg({ idle_timeout_minutes: data.idle_timeout_minutes, warning_minutes: data.warning_minutes });
          }
        }).catch(() => { /* server 401 handled globally */ });
      }
    };
    const opts = { passive: true, capture: true };
    // Real human activity — background polling/timers explicitly NOT included.
    window.addEventListener("click", record, opts);
    window.addEventListener("keydown", record, opts);
    window.addEventListener("scroll", record, opts);
    window.addEventListener("touchstart", record, opts);
    return () => {
      window.removeEventListener("click", record, opts);
      window.removeEventListener("keydown", record, opts);
      window.removeEventListener("scroll", record, opts);
      window.removeEventListener("touchstart", record, opts);
    };
  }, [isLoggedIn, showWarning]);

  // Local countdown tick — evaluate against last activity every second.
  useEffect(() => {
    if (!isLoggedIn) return;
    const timeoutMs = cfg.idle_timeout_minutes * 60_000;
    const warnMs = cfg.warning_minutes * 60_000;
    const tick = () => {
      const idleMs = Date.now() - lastActivityRef.current;
      const untilExpiryMs = timeoutMs - idleMs;
      if (untilExpiryMs <= 0) {
        // Idle over threshold → force logout via peer-safe broadcast + auth.logout.
        try { bcRef.current?.postMessage({ type: "logout", ts: Date.now() }); } catch { /* ignore */ }
        try { window.sessionStorage.setItem("ens_idle_timeout_reason", "1"); } catch { /* ignore */ }
        logout();
        return;
      }
      if (untilExpiryMs <= warnMs) {
        setShowWarning(true);
        setRemainingSec(Math.max(0, Math.ceil(untilExpiryMs / 1000)));
      } else if (showWarning) {
        setShowWarning(false);
      }
    };
    const iv = setInterval(tick, 1_000);
    return () => clearInterval(iv);
  }, [isLoggedIn, cfg.idle_timeout_minutes, cfg.warning_minutes, logout, showWarning]);

  const extendNow = useCallback(async () => {
    try {
      await api.post("/auth/extend-session");
      lastActivityRef.current = Date.now();
      lastServerTouchRef.current = Date.now();
      setShowWarning(false);
      try { bcRef.current?.postMessage({ type: "activity", ts: Date.now() }); } catch { /* ignore */ }
    } catch { /* 401 handled globally */ }
  }, []);

  const mm = Math.floor(remainingSec / 60);
  const ss = String(remainingSec % 60).padStart(2, "0");

  return (
    <>
      {children}
      {isLoggedIn && showWarning && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="idle-warning-overlay">
          <div className="bg-white border-2 border-amber-500 shadow-2xl max-w-md w-full p-6" data-testid="idle-warning-dialog">
            <h2 className="font-display text-2xl tracking-tight text-[#0B1D3A] mb-2">SESI AKAN BERAKHIR</h2>
            <p className="text-sm text-slate-700 mb-3">
              Tidak ada aktivitas selama hampir {cfg.idle_timeout_minutes} menit. Demi keamanan,
              Anda akan keluar otomatis.
            </p>
            <div className="text-center bg-amber-50 border border-amber-300 py-3 my-3">
              <div className="text-[10px] uppercase tracking-wider text-amber-700">Waktu tersisa</div>
              <div className="font-display text-4xl tabular-nums text-amber-800" data-testid="idle-countdown">{mm}:{ss}</div>
            </div>
            <div className="text-[11px] text-slate-500 italic mb-3">
              Perhatian: perubahan form yang belum disimpan dapat hilang saat logout otomatis.
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                data-testid="idle-btn-logout-now"
                onClick={() => logout()}
                className="px-4 py-2 text-xs uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Keluar Sekarang
              </button>
              <button
                type="button"
                data-testid="idle-btn-extend"
                onClick={extendNow}
                className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-[#FF5A1F] hover:bg-[#e04a13] text-white"
              >
                Lanjutkan Sesi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
